// Multiverse theme runtime. Shared helpers and components are added in later tasks;
// this file establishes the global namespace and defines shared Multiverse components.

window.Multiverse = window.Multiverse || {};
Multiverse.defineOnce = (name, cls) => {
  if (!customElements.get(name)) customElements.define(name, cls);
};
// Scroll direction helpers shared by the sticky bars: iOS overscroll reports scrollY outside
// the document, so readers clamp it and ignore movements smaller than SCROLL_JITTER px.
Multiverse.SCROLL_JITTER = 5;
Multiverse.clampScrollY = () => {
  const max = Math.max(document.documentElement.scrollHeight - window.innerHeight, 0);
  return Math.min(Math.max(window.scrollY, 0), max);
};

// ---- Material Symbols loader ----
// Icon blocks render <span class="mv-symbol">icon_name</span> (snippets/symbol-icon.liquid).
// Google Fonts serves a subset of the variable icon font per `icon_names=` list, but every
// subset declares the same @font-face, so two subset stylesheets on one page would override
// each other (only the last one's glyphs render). One stylesheet is therefore built here for
// the union of every icon on the page, re-issued as a superset when new icons appear (editor
// adds a block, section reloads). Symbols stay `visibility: hidden` (multiverse.css) until
// their glyphs are loaded, so the ligature text never flashes.
(() => {
  const FAMILY = 'Material Symbols Outlined';
  const CSS_URL = 'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,0&display=block&icon_names=';
  const READY_CLASS = 'mv-symbol--ready';
  const FALLBACK_CLASS = 'mv-symbol--fallback';
  const FALLBACK_MS = 8000;
  const ready = new Set();
  let requested = '';
  let current = null;
  let timer = 0;
  let fallbackTimer = 0;

  const reveal = () => {
    document.querySelectorAll('.mv-symbol').forEach((el) => {
      if (ready.has(el.textContent.trim())) el.classList.add(READY_CLASS);
    });
  };

  // Google Fonts unreachable (blocked, offline, timed out): show the ligature text rather than
  // hiding the icons forever. Cleared as soon as a stylesheet + font load succeeds.
  const fallback = () => {
    window.clearTimeout(fallbackTimer);
    fallbackTimer = 0;
    document.querySelectorAll('.mv-symbol').forEach((el) => el.classList.add(FALLBACK_CLASS));
  };

  const load = () => {
    timer = 0;
    const names = new Set(ready);
    document.querySelectorAll('.mv-symbol').forEach((el) => {
      const name = el.textContent.trim();
      if (name) names.add(name);
    });
    const list = [...names].sort().join(',');
    if (!list || list === requested) { reveal(); return; }
    requested = list;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.media = 'print';
    link.dataset.mvSymbols = '';
    link.href = CSS_URL + list;
    link.onload = () => {
      link.media = 'all';
      const done = () => {
        if (requested !== list) return; // a newer superset is on its way
        window.clearTimeout(fallbackTimer);
        fallbackTimer = 0;
        list.split(',').forEach((name) => ready.add(name));
        if (current && current !== link) current.remove();
        current = link;
        document.querySelectorAll('.' + FALLBACK_CLASS).forEach((el) => el.classList.remove(FALLBACK_CLASS));
        reveal();
      };
      if (document.fonts && document.fonts.load) {
        document.fonts.load(`24px "${FAMILY}"`, list.replace(/,/g, ' ')).then(done, fallback);
      } else {
        done();
      }
    };
    link.onerror = fallback;
    window.clearTimeout(fallbackTimer);
    fallbackTimer = window.setTimeout(fallback, FALLBACK_MS);
    document.head.append(link);
  };

  const schedule = () => {
    if (timer) return;
    timer = window.setTimeout(load, 30);
  };

  Multiverse.loadSymbols = schedule;
  const start = () => {
    schedule();
    document.addEventListener('shopify:section:load', schedule);
    new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node.nodeType === 1 && (node.matches('.mv-symbol') || node.querySelector('.mv-symbol'))) {
            schedule();
            return;
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  };
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', start, { once: true }) : start();
})();

// ---- splide-component ----
(() => {
  const MOBILE_BREAKPOINT = 749;

  class SplideComponent extends HTMLElement {
    connectedCallback() {
      this._onSectionLoad = this._onSectionLoad || ((event) => {
        if (event.target === this || (event.target && event.target.contains && event.target.contains(this))) {
          this.mount();
        }
      });
      document.addEventListener('shopify:section:load', this._onSectionLoad);
      this.mount();
    }

    disconnectedCallback() {
      document.removeEventListener('shopify:section:load', this._onSectionLoad);
      this.destroy();
    }

    mount() {
      this.destroy();

      const root = this.querySelector('.splide');
      if (!root) return;

      if (typeof window.Splide === 'undefined') {
        // splide.min.js is also loaded with `defer`; it should already have executed
        // (it's declared before multiverse.js in theme.liquid) but guard against any
        // load-order edge case rather than failing silently forever.
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.mount(), { once: true });
        } else if (!this._retryTimer) {
          this._retryTimer = window.setTimeout(() => {
            this._retryTimer = null;
            this.mount();
          }, 50);
        }
        return;
      }

      const type = this.dataset.type || 'slide';
      const autoplay = this.dataset.autoplay === 'true';
      const autoplaySpeed = parseFloat(this.dataset.autoplaySpeed) || 5;
      const slidesDesktop = parseInt(this.dataset.slidesDesktop, 10) || 0;
      const slidesMobile = parseInt(this.dataset.slidesMobile, 10) || 0;
      const gapDesktop = parseInt(this.dataset.gapDesktop, 10) || 0;
      const gapMobile = parseInt(this.dataset.gapMobile, 10) || 0;
      const arrows = this.dataset.arrows === 'true';
      const pagination = this.dataset.pagination === 'true';
      const destroyDesktop = this.dataset.destroyDesktop === 'true';
      const destroyMobile = this.dataset.destroyMobile === 'true';

      const options = {
        type,
        autoplay,
        interval: autoplaySpeed * 1000,
        arrows,
        pagination,
        gap: `${gapDesktop}px`,
        destroy: destroyDesktop,
        breakpoints: {
          [MOBILE_BREAKPOINT]: {
            gap: `${gapMobile}px`,
            destroy: destroyMobile,
            ...(slidesMobile > 0 ? { perPage: slidesMobile, autoWidth: false } : { autoWidth: true }),
          },
        },
      };

      if (slidesDesktop > 0) {
        options.perPage = slidesDesktop;
        options.autoWidth = false;
      } else {
        options.autoWidth = true;
      }

      this.splide = new window.Splide(root, options);
      this.splide.mount();
    }

    destroy() {
      if (this.splide) {
        this.splide.destroy();
        this.splide = null;
      }
      if (this._retryTimer) {
        window.clearTimeout(this._retryTimer);
        this._retryTimer = null;
      }
    }
  }

  Multiverse.defineOnce('splide-component', SplideComponent);
})();

// ---- mv-text-animation ----
// Animates the text of its first element child (the "target"). Contract:
//   data-animation   split | shiny | blur | typing | highlight-swipe | highlight-fade-up |
//                    highlight-underline | highlight-strike
//   data-on-scroll   "true" waits until the host scrolls into view, otherwise plays at once
//   data-split-type  chars | words                      (split, blur)
//   data-bold-only   "true" limits the marker to <strong>/<b> (highlight-*), else all text
//   data-typing-*    speed, initial-delay, pause, deleting-speed (ms); loop, reverse,
//                    hide-cursor-while-typing ("true"/"false"); cursor (character; absent = none)
// Timing/colour values are CSS custom properties on the host (--ta-*), see component-heading.css.
// The JS adds `mv-ta-target` to the target, `is-visible` when the animation should play and
// `mv-ta-reduced` under prefers-reduced-motion (final state, no motion). Splitting wraps text
// nodes only, so inline tags (highlights, links) survive. Instances tear down and restore the
// original markup on disconnect, so section reloads in the editor simply re-run them.
(() => {
  const BLOCK_TAGS = new Set(['P', 'DIV', 'LI', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'UL', 'OL']);
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const textNodes = (root) => {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        if (!node.data.trim()) return NodeFilter.FILTER_REJECT;
        const parent = node.parentElement;
        if (parent && parent.closest('svg, script, style')) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      },
    });
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    return nodes;
  };

  class TextAnimation extends HTMLElement {
    connectedCallback() {
      if (this._active) return;
      this._active = true;
      this._timers = new Set();
      this._original = this.innerHTML;
      this.classList.remove('is-visible', 'mv-ta-reduced');
      const target = this.firstElementChild;
      if (!target) return;
      target.classList.add('mv-ta-target');
      this._target = target;
      this._reduced = reducedMotion();
      if (this._reduced) this.classList.add('mv-ta-reduced');

      const type = this.dataset.animation;
      const ready = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
      const token = (this._token = {});
      ready.then(() => {
        if (this._token !== token || !this.isConnected) return;
        switch (type) {
          case 'split':
          case 'blur':
            this.setupSplit();
            break;
          case 'typing':
            this.setupTyping();
            break;
          case 'shiny':
            if (!this._reduced) this.whenVisible(() => this.classList.add('is-visible'));
            break;
          default:
            if (type && type.startsWith('highlight')) this.setupHighlight();
        }
      });
    }

    disconnectedCallback() {
      this._active = false;
      this._token = null;
      if (this._observer) this._observer.disconnect();
      this._observer = null;
      this._timers.forEach((id) => window.clearTimeout(id));
      this._timers.clear();
      this.classList.remove('is-visible', 'mv-ta-reduced');
      this.innerHTML = this._original;
    }

    later(fn, ms) {
      const id = window.setTimeout(() => {
        this._timers.delete(id);
        if (this._active) fn();
      }, ms);
      this._timers.add(id);
      return id;
    }

    // Runs fn when the host should start: immediately, or once scrolled into view.
    whenVisible(fn) {
      if (this.dataset.onScroll === 'true' && 'IntersectionObserver' in window) {
        this._observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this._observer.disconnect();
            this._observer = null;
            fn();
          },
          { threshold: 0.1 }
        );
        this._observer.observe(this);
      } else {
        window.requestAnimationFrame(() => this._active && fn());
      }
    }

    // Commits the hidden state to layout, then flips to visible so the transitions run.
    reveal() {
      void this.offsetWidth;
      this.classList.add('is-visible');
    }

    setupSplit() {
      const target = this._target;
      const label = target.textContent.replace(/\s+/g, ' ').trim();
      if (label) target.setAttribute('aria-label', label);
      if (this._reduced) {
        this.classList.add('is-visible');
        return;
      }
      const byChars = this.dataset.splitType === 'chars';
      let index = 0;
      textNodes(target).forEach((node) => {
        const fragment = document.createDocumentFragment();
        const parts = byChars ? Array.from(node.data) : node.data.split(/(\s+)/);
        parts.forEach((part) => {
          if (!part) return;
          if (/^\s+$/.test(part)) {
            fragment.appendChild(document.createTextNode(part));
            return;
          }
          const unit = document.createElement('span');
          unit.className = 'mv-ta-unit';
          unit.style.setProperty('--i', index++);
          unit.textContent = part;
          fragment.appendChild(unit);
        });
        node.parentNode.replaceChild(fragment, node);
      });
      this.whenVisible(() => this.reveal());
    }

    setupHighlight() {
      const target = this._target;
      let marks;
      if (this.dataset.boldOnly === 'true') {
        marks = [...target.querySelectorAll('strong, b')];
      } else {
        const containers = [...target.children].filter((child) => BLOCK_TAGS.has(child.tagName));
        marks = (containers.length ? containers : [target]).map((container) => {
          const span = document.createElement('span');
          while (container.firstChild) span.appendChild(container.firstChild);
          container.appendChild(span);
          return span;
        });
      }
      marks.forEach((mark) => mark.classList.add('mv-ta-hl'));
      if (this._reduced) {
        this.classList.add('is-visible');
        return;
      }
      this.whenVisible(() => this.reveal());
    }

    setupTyping() {
      const target = this._target;
      const nodes = textNodes(target);
      const full = nodes.map((node) => node.data);
      const total = full.reduce((sum, text) => sum + text.length, 0);
      const label = target.textContent.replace(/\s+/g, ' ').trim();
      if (label) target.setAttribute('aria-label', label);
      if (this._reduced || !total) return;

      const d = this.dataset;
      const speed = parseInt(d.typingSpeed, 10) || 50;
      const initialDelay = parseInt(d.typingInitialDelay, 10) || 0;
      const pause = parseInt(d.typingPause, 10) || 0;
      const deletingSpeed = parseInt(d.typingDeletingSpeed, 10) || 30;
      const loop = d.typingLoop === 'true';
      const reverse = d.typingReverse === 'true';
      const hideWhileTyping = d.typingHideCursorWhileTyping === 'true';
      const cursorChar = 'typingCursor' in d ? d.typingCursor : '';

      // Keeps the line box from collapsing while nothing is typed yet.
      const holder = document.createTextNode('​');
      const firstNode = nodes[0];
      firstNode.parentNode.insertBefore(holder, firstNode);

      let cursor = null;
      if (cursorChar) {
        cursor = document.createElement('span');
        cursor.className = 'mv-ta-cursor';
        cursor.setAttribute('aria-hidden', 'true');
        cursor.textContent = cursorChar;
        // Sits at the writing end, inside the last (or first, when reversed) block-level child.
        const edge = reverse ? nodes[0] : nodes[nodes.length - 1];
        let container = edge.parentNode;
        while (container !== target && container.parentNode !== target) container = container.parentNode;
        if (!BLOCK_TAGS.has(container.tagName)) container = target;
        if (reverse) container.insertBefore(cursor, container.firstChild);
        else container.appendChild(cursor);
      }
      const showCursor = (show) => cursor && cursor.classList.toggle('is-hidden', !show);

      const render = (count) => {
        let remaining = count;
        const order = reverse ? [...nodes.keys()].reverse() : [...nodes.keys()];
        order.forEach((i) => {
          const text = full[i];
          const shown = Math.max(0, Math.min(remaining, text.length));
          remaining -= shown;
          nodes[i].data = reverse ? text.slice(text.length - shown) : text.slice(0, shown);
        });
      };

      let count = 0;
      let deleting = false;
      const step = () => {
        if (!deleting) {
          count += 1;
          render(count);
          if (count < total) {
            if (hideWhileTyping) showCursor(false);
            this.later(step, speed);
            return;
          }
          showCursor(true);
          if (loop) {
            this.later(() => {
              deleting = true;
              if (hideWhileTyping) showCursor(false);
              step();
            }, pause);
          }
          return;
        }
        count -= 1;
        render(count);
        if (count > 0) {
          this.later(step, deletingSpeed);
          return;
        }
        deleting = false;
        showCursor(true);
        this.later(step, pause);
      };

      render(0);
      this.whenVisible(() => this.later(step, initialDelay));
    }
  }

  Multiverse.defineOnce('mv-text-animation', TextAnimation);
})();

// ---- countdown-timer ----
// Ticks once a second and writes the remaining time into its [data-unit="days|hours|minutes|seconds"]
// children (a unit that is not present rolls into the next smaller one, so hiding "days" makes
// hours count past 24). If a unit contains [data-digit] children, each digit gets one character.
// Contract:
//   data-type              fixed | evergreen | daily
//   data-end               ISO date-time for `fixed` (no offset = the visitor's local time)
//   data-duration-minutes  `evergreen`: minutes from the visitor's first visit; the start time is
//                          kept in localStorage under `mv-countdown-<data-key>`
//   data-daily-end         `daily`: HH:MM local time, every day
//   data-repeat-minutes    `fixed` + restart: once the end has passed, move it forward by this
//                          many minutes until it is in the future (e.g. 1440 = same time next day)
//   data-expired-action    hide | restart | show-text
//                          hide: adds `is-expired` to the host and to its closest
//                          `[data-countdown-wrap]` / `.mv-countdown-wrap`; restart: fixed → repeat,
//                          evergreen → new start now, daily → next day; show-text: reveals the
//                          host's [data-expired-text] child and hides the units.
// Fires `countdown:expired` on the host when the end is reached.
(() => {
  const STORAGE_PREFIX = 'mv-countdown-';
  const pad = (n, width) => String(n).padStart(width, '0');

  class CountdownTimer extends HTMLElement {
    connectedCallback() {
      // Upgraded during parsing (script not deferred): children are not there yet.
      if (document.readyState === 'loading' && !this.querySelector('[data-unit]')) {
        document.addEventListener('DOMContentLoaded', () => { if (this.isConnected) this.connectedCallback(); }, { once: true });
        return;
      }
      this.units = {};
      for (const el of this.querySelectorAll('[data-unit]')) this.units[el.dataset.unit] = el;
      this.expiredText = this.querySelector('[data-expired-text]');
      this.wrap = this.closest('[data-countdown-wrap], .mv-countdown-wrap') || this;
      this.wrap.classList.remove('is-expired');
      this.end = this.computeEnd();
      this.tick();
      window.clearInterval(this.timer);
      this.timer = window.setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
      this.timer = null;
    }

    get storageKey() {
      return STORAGE_PREFIX + (this.dataset.key || 'default');
    }

    readStorage() {
      try { return window.localStorage.getItem(this.storageKey); } catch (e) { return null; }
    }

    writeStorage(value) {
      try { window.localStorage.setItem(this.storageKey, value); } catch (e) { /* private mode */ }
    }

    // Returns the end time as a Date, or null when the configuration cannot be parsed.
    computeEnd(restart = false) {
      const type = this.dataset.type || 'fixed';
      const now = Date.now();
      if (type === 'evergreen') {
        const minutes = parseFloat(this.dataset.durationMinutes) || 0;
        let start = parseInt(this.readStorage(), 10);
        if (restart || !start || Number.isNaN(start)) {
          start = now;
          this.writeStorage(String(start));
        }
        return new Date(start + minutes * 60000);
      }
      if (type === 'daily') {
        const [h, m] = (this.dataset.dailyEnd || '00:00').split(':').map((v) => parseInt(v, 10) || 0);
        const end = new Date();
        end.setHours(h, m, 0, 0);
        if (end.getTime() <= now) end.setDate(end.getDate() + 1);
        return end;
      }
      const end = new Date(this.dataset.end || '');
      if (Number.isNaN(end.getTime())) return null;
      const repeat = parseFloat(this.dataset.repeatMinutes) || 0;
      if (restart && repeat > 0) {
        while (end.getTime() <= now) end.setTime(end.getTime() + repeat * 60000);
      }
      return end;
    }

    tick() {
      const remaining = this.end ? this.end.getTime() - Date.now() : 0;
      if (remaining <= 0) {
        this.render(0);
        this.expire();
        return;
      }
      this.render(remaining);
    }

    expire() {
      const action = this.dataset.expiredAction || 'hide';
      this.dispatchEvent(new CustomEvent('countdown:expired', { bubbles: true }));
      if (action === 'restart') {
        const next = this.computeEnd(true);
        if (next && next.getTime() > Date.now()) {
          this.end = next;
          this.tick();
          return;
        }
      }
      window.clearInterval(this.timer);
      this.timer = null;
      if (action === 'show-text' && this.expiredText) {
        this.classList.add('is-expired');
        this.expiredText.hidden = false;
        for (const el of Object.values(this.units)) el.parentElement.hidden = true;
        return;
      }
      if (action === 'hide' || action === 'restart') {
        this.classList.add('is-expired');
        this.wrap.classList.add('is-expired');
      }
    }

    render(ms) {
      const total = Math.floor(ms / 1000);
      const { units } = this;
      let seconds = total;
      const values = {};
      if (units.days) { values.days = Math.floor(seconds / 86400); seconds -= values.days * 86400; }
      if (units.hours) { values.hours = Math.floor(seconds / 3600); seconds -= values.hours * 3600; }
      if (units.minutes) { values.minutes = Math.floor(seconds / 60); seconds -= values.minutes * 60; }
      if (units.seconds) values.seconds = seconds;
      for (const [unit, el] of Object.entries(units)) {
        const text = pad(values[unit] || 0, 2);
        if (el.dataset.value === text) continue;
        el.dataset.value = text;
        const digits = el.querySelectorAll('[data-digit]');
        if (digits.length) {
          // extra characters (e.g. 3-digit days) go into the first digit box
          const head = text.slice(0, text.length - digits.length + 1);
          digits.forEach((d, i) => { d.textContent = i === 0 ? head : text.charAt(text.length - digits.length + i); });
        } else {
          el.textContent = text;
        }
      }
    }
  }

  Multiverse.defineOnce('countdown-timer', CountdownTimer);
})();

// ---- dynamic-dates ----
// Replaces the tokens [today], [tomorrow] and [+Nd] (N days from today) in its text with a
// formatted date. Contract:
//   data-format   short | long | weekday   (Intl.DateTimeFormat presets, default `long`)
//   data-locale   BCP 47 locale, default the document language
// Only text nodes are touched, so links and bold spans around a token survive. Runs again when
// re-attached (section reload in the editor); the original markup is restored on disconnect.
(() => {
  const TOKEN = /\[(today|tomorrow|\+(\d+)d)\]/gi;
  const FORMATS = {
    short: { day: 'numeric', month: 'short' },
    long: { day: 'numeric', month: 'long' },
    weekday: { weekday: 'long', day: 'numeric', month: 'long' },
  };

  class DynamicDates extends HTMLElement {
    connectedCallback() {
      if (document.readyState === 'loading' && !this.firstChild) {
        document.addEventListener('DOMContentLoaded', () => { if (this.isConnected) this.connectedCallback(); }, { once: true });
        return;
      }
      if (this.original == null) this.original = this.innerHTML;
      const locale = this.dataset.locale || document.documentElement.lang || undefined;
      const options = FORMATS[this.dataset.format] || FORMATS.long;
      let formatter;
      try { formatter = new Intl.DateTimeFormat(locale, options); } catch (e) { formatter = new Intl.DateTimeFormat(undefined, options); }
      const format = (offsetDays) => {
        const d = new Date();
        d.setDate(d.getDate() + offsetDays);
        return formatter.format(d);
      };
      const walker = document.createTreeWalker(this, NodeFilter.SHOW_TEXT);
      const nodes = [];
      while (walker.nextNode()) nodes.push(walker.currentNode);
      for (const node of nodes) {
        if (!TOKEN.test(node.nodeValue)) { TOKEN.lastIndex = 0; continue; }
        TOKEN.lastIndex = 0;
        node.nodeValue = node.nodeValue.replace(TOKEN, (match, word, days) => {
          if (days !== undefined) return format(parseInt(days, 10));
          return format(word.toLowerCase() === 'tomorrow' ? 1 : 0);
        });
      }
    }

    disconnectedCallback() {
      if (this.original != null) this.innerHTML = this.original;
    }
  }

  Multiverse.defineOnce('dynamic-dates', DynamicDates);
})();

// ---- copy-button ----
// Copies its data-code to the clipboard on click / Enter / Space and adds `success` for 2 s.
// Uses navigator.clipboard when available (secure contexts) and falls back to a temporary
// textarea + document.execCommand('copy') otherwise. Fires `copy-button:copied` on success.
(() => {
  const SUCCESS_MS = 2000;

  const fallbackCopy = (text) => {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.cssText = 'position:fixed;top:0;left:0;opacity:0;pointer-events:none;';
    document.body.appendChild(area);
    area.select();
    let ok = false;
    try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
    area.remove();
    return ok;
  };

  class CopyButton extends HTMLElement {
    connectedCallback() {
      if (!this.hasAttribute('role')) this.setAttribute('role', 'button');
      if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');
      this.onClick = this.onClick || ((event) => { event.preventDefault(); this.copy(); });
      this.onKey = this.onKey || ((event) => {
        if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); this.copy(); }
      });
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKey);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKey);
      window.clearTimeout(this.resetTimer);
    }

    async copy() {
      const text = (this.dataset.code || '').trim();
      if (!text) return;
      let ok = false;
      if (navigator.clipboard && window.isSecureContext) {
        try { await navigator.clipboard.writeText(text); ok = true; } catch (e) { ok = false; }
      }
      if (!ok) ok = fallbackCopy(text);
      if (!ok) return;
      this.classList.add('success');
      this.dispatchEvent(new CustomEvent('copy-button:copied', { bubbles: true, detail: { code: text } }));
      window.clearTimeout(this.resetTimer);
      this.resetTimer = window.setTimeout(() => this.classList.remove('success'), SUCCESS_MS);
    }
  }

  Multiverse.defineOnce('copy-button', CopyButton);
})();

// ---- sticky-announcement ----
// Wraps the announcement bar's content when the section's `sticky_type` is not "none". The
// element itself is display:block and stays in flow; what sticks is the section wrapper
// (.announcement-bar-section — the element Shopify renders around the section and a direct
// child of <body>, so it has the whole page to stick within). Contract:
//   data-sticky-type   always        → wrapper gets `announcement-bar-section--sticky`
//                                      (position: sticky; top: 0; z-index above the header)
//                      on-scroll-up  → same, plus `is-hidden` (translateY(-100%)) while the
//                                      page scrolls down past the bar; removed on scroll up
//                                      or at the top of the page
//   --announcement-height on :root  → the bar's current height in px (0px while hidden),
//                                      kept fresh by a ResizeObserver; the header uses it to
//                                      offset its own sticky position. Removed on disconnect.
// Scroll handling is a passive listener that coalesces into one requestAnimationFrame.
(() => {
  const HEIGHT_PROPERTY = '--announcement-height';
  const STICKY_CLASS = 'announcement-bar-section--sticky';
  const HIDDEN_CLASS = 'is-hidden';

  class StickyAnnouncement extends HTMLElement {
    connectedCallback() {
      this.target = this.closest('.announcement-bar-section') || this.parentElement || this;
      this.isHidden = false;
      this.lastY = window.scrollY;
      this.frame = 0;

      this.onScroll = () => {
        if (this.frame) return;
        this.frame = window.requestAnimationFrame(() => {
          this.frame = 0;
          this.update();
        });
      };
      this.onSectionSelect = (event) => {
        if (this.target.contains(event.target)) this.reveal();
      };
      this.publishHeight = () => {
        const height = this.isHidden ? 0 : this.offsetHeight;
        document.documentElement.style.setProperty(HEIGHT_PROPERTY, `${height}px`);
      };

      this.target.classList.add(STICKY_CLASS);
      this.target.classList.remove(HIDDEN_CLASS);
      this.resizeObserver = new ResizeObserver(this.publishHeight);
      this.resizeObserver.observe(this);
      if (this.dataset.stickyType === 'on-scroll-up') {
        window.addEventListener('scroll', this.onScroll, { passive: true });
        document.addEventListener('shopify:section:select', this.onSectionSelect);
      }
      this.publishHeight();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('shopify:section:select', this.onSectionSelect);
      if (this.frame) window.cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.resizeObserver.disconnect();
      this.target.classList.remove(STICKY_CLASS, HIDDEN_CLASS);
      document.documentElement.style.removeProperty(HEIGHT_PROPERTY);
    }

    update() {
      // iOS rubber-banding reports scrollY below 0 / past the end; clamp so the overscroll
      // bounce cannot flip the bar, and ignore sub-threshold jitter between frames.
      const y = Multiverse.clampScrollY();
      const delta = y - this.lastY;
      if (y > 0 && Math.abs(delta) < Multiverse.SCROLL_JITTER) return;
      this.lastY = y;
      if (y <= 0 || delta < 0) {
        this.reveal();
      } else if (delta > 0 && y > this.offsetHeight) {
        this.hide();
      }
    }

    hide() {
      if (this.isHidden) return;
      this.isHidden = true;
      this.target.classList.add(HIDDEN_CLASS);
      this.publishHeight();
    }

    reveal() {
      if (!this.isHidden) return;
      this.isHidden = false;
      this.target.classList.remove(HIDDEN_CLASS);
      this.publishHeight();
    }
  }

  Multiverse.defineOnce('sticky-announcement', StickyAnnouncement);
})();

// ---- sticky-header ----
// Wraps the header section's content (sections/header.liquid). What sticks is the section
// wrapper (.section-header, a direct child of <body>). Contract:
//   data-sticky-type   none              → nothing sticks; only --header-height is published
//                      always            → wrapper gets `mv-header-sticky` (position: sticky,
//                                          top: var(--announcement-height))
//                      reduce-logo-size  → same as always; the logo shrinks via the
//                                          `scrolled-past-header` class (CSS in section-header.css)
//                      on-scroll-up      → sticky, plus `is-hidden` (top: -header height) while the
//                                          page scrolls down past the header; removed on scroll up
//   scrolled-past-header on the wrapper  → the page has scrolled past the header's natural position
//   --header-height on :root             → the header's current height in px (ResizeObserver)
//   .preventHide (property)              → set by Dawn's localization-form.js to keep the header
//                                          visible while a selector is open; honoured here
// Hiding closes open desktop menus; it is skipped while the search modal or the drawer is open.
(() => {
  const STICKY_CLASS = 'mv-header-sticky';
  const HIDDEN_CLASS = 'is-hidden';
  const PAST_CLASS = 'scrolled-past-header';

  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section') || this.parentElement || this;
      this.type = this.dataset.stickyType || 'none';
      this.isHidden = false;
      this.lastY = window.scrollY;
      this.frame = 0;
      this.preventHide = false;

      this.publishHeight = () => {
        document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
      };
      this.onScroll = () => {
        if (this.frame) return;
        this.frame = window.requestAnimationFrame(() => {
          this.frame = 0;
          this.update();
        });
      };
      this.onSectionSelect = (event) => {
        if (this.section.contains(event.target)) this.reveal();
      };

      this.resizeObserver = new ResizeObserver(this.publishHeight);
      this.resizeObserver.observe(this);
      this.publishHeight();

      if (this.type !== 'none') {
        // A zero-height static sibling marks the header's natural position: offsetTop of the
        // sticky wrapper itself moves with the page once it is stuck.
        this.sentinel = document.createElement('div');
        this.sentinel.className = 'mv-header-sentinel';
        this.sentinel.setAttribute('aria-hidden', 'true');
        this.sentinel.style.cssText = 'display:block;position:static;height:0;margin:0;padding:0;border:0;';
        this.section.parentNode.insertBefore(this.sentinel, this.section);
        this.section.classList.add(STICKY_CLASS);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        document.addEventListener('shopify:section:select', this.onSectionSelect);
        this.update();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('shopify:section:select', this.onSectionSelect);
      if (this.frame) window.cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.resizeObserver.disconnect();
      this.sentinel?.remove();
      this.section.classList.remove(STICKY_CLASS, HIDDEN_CLASS, PAST_CLASS);
      document.documentElement.style.removeProperty('--header-height');
    }

    // The header's natural bottom edge in document coordinates (where it sits when not stuck).
    naturalBottom() {
      const top = this.sentinel ? this.sentinel.getBoundingClientRect().top + window.scrollY : this.section.offsetTop;
      return top + this.offsetHeight;
    }

    update() {
      // Clamp iOS overscroll (see sticky-announcement) and ignore sub-threshold jitter.
      const y = Multiverse.clampScrollY();
      const delta = y - this.lastY;
      const past = y > this.naturalBottom();
      this.section.classList.toggle(PAST_CLASS, past);

      if (this.type !== 'on-scroll-up') return;
      if (y > 0 && Math.abs(delta) < Multiverse.SCROLL_JITTER) return;
      this.lastY = y;
      if (y <= 0 || delta < 0) {
        this.reveal();
      } else if (delta > 0 && past && !this.preventHide && !this.hasOpenOverlay()) {
        this.hide();
      }
    }

    hasOpenOverlay() {
      return Boolean(
        this.querySelector('details-modal details[open]') || this.querySelector('header-drawer.is-open')
      );
    }

    hide() {
      if (this.isHidden) return;
      this.isHidden = true;
      this.section.classList.add(HIDDEN_CLASS);
      this.querySelectorAll('header-menu, products-mega-menu').forEach((menu) => menu.close && menu.close());
    }

    reveal() {
      if (!this.isHidden) return;
      this.isHidden = false;
      this.section.classList.remove(HIDDEN_CLASS);
    }
  }

  Multiverse.defineOnce('sticky-header', StickyHeader);
})();

// ---- header-menu / products-mega-menu ----
// Desktop menu disclosures (snippets/header-dropdown-menu.liquid, header-mega-menu.liquid,
// header-products-mega-menu.liquid). Each wraps one <details>. Contract:
//   hover (mouse pointers only) opens after a short intent delay and closes shortly after the
//   pointer leaves; click/Enter on the summary toggles; Escape closes and refocuses the summary;
//   focus leaving the element closes it; opening one closes every other open menu; nested
//   <details> fly-outs inside a dropdown open on hover too. `close()` is public (sticky-header).
//   `is-open` mirrors the open state on the element for styling/animation.
// <products-mega-menu> adds tabs: hovering or focusing an [data-pmega-tab="N"] link shows the
// matching [data-pmega-panel="N"] and marks its <li> `is-active`.
(() => {
  const OPEN_DELAY = 80;
  const CLOSE_DELAY = 160;

  class MenuDisclosure extends HTMLElement {
    constructor() {
      super();
      // Handlers are created once here so a reconnect (editor reorder moves the element) can
      // re-add them without double-binding: addEventListener ignores an identical listener.
      this.openedByHover = false;
      this.onSummaryClick = (event) => {
        event.preventDefault();
        if (!this.details.open) {
          this.open();
        } else if (this.openedByHover) {
          // The pointer that hovered it open is now clicking it: keep it open and treat the
          // menu as click-owned from here on, so the next click closes it.
          this.openedByHover = false;
        } else {
          this.close();
        }
      };
      this.onPointerEnter = (event) => {
        if (event.pointerType && event.pointerType !== 'mouse') return;
        window.clearTimeout(this.closeTimer);
        this.openTimer = window.setTimeout(() => this.open({ byHover: true }), OPEN_DELAY);
      };
      this.onPointerLeave = (event) => {
        if (event.pointerType && event.pointerType !== 'mouse') return;
        window.clearTimeout(this.openTimer);
        this.closeTimer = window.setTimeout(() => this.close(), CLOSE_DELAY);
      };
      this.onKeydown = (event) => {
        if (event.key !== 'Escape' || !this.details.open) return;
        event.preventDefault();
        this.close();
        this.summary.focus();
      };
      this.onFocusOut = (event) => {
        if (event.relatedTarget && this.contains(event.relatedTarget)) return;
        window.setTimeout(() => {
          if (!this.contains(document.activeElement)) this.close();
        });
      };
      this.onNestedEnter = (event) => {
        const nested = event.currentTarget;
        nested.open = true;
        nested.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'true');
      };
      this.onNestedLeave = (event) => {
        const nested = event.currentTarget;
        nested.open = false;
        nested.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
      };
    }

    connectedCallback() {
      this.details = this.querySelector(':scope > details');
      this.summary = this.details && this.details.querySelector(':scope > summary');
      if (!this.details || !this.summary) return;

      this.summary.addEventListener('click', this.onSummaryClick);
      this.addEventListener('pointerenter', this.onPointerEnter);
      this.addEventListener('pointerleave', this.onPointerLeave);
      this.addEventListener('keydown', this.onKeydown);
      this.addEventListener('focusout', this.onFocusOut);
      this.nested = this.querySelectorAll('details details');
      this.nested.forEach((nested) => {
        nested.addEventListener('pointerenter', this.onNestedEnter);
        nested.addEventListener('pointerleave', this.onNestedLeave);
      });
    }

    disconnectedCallback() {
      window.clearTimeout(this.openTimer);
      window.clearTimeout(this.closeTimer);
      if (!this.details || !this.summary) return;
      this.summary.removeEventListener('click', this.onSummaryClick);
      this.removeEventListener('pointerenter', this.onPointerEnter);
      this.removeEventListener('pointerleave', this.onPointerLeave);
      this.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('focusout', this.onFocusOut);
      this.nested && this.nested.forEach((nested) => {
        nested.removeEventListener('pointerenter', this.onNestedEnter);
        nested.removeEventListener('pointerleave', this.onNestedLeave);
      });
    }

    open({ byHover = false } = {}) {
      if (!this.details || this.details.open) return;
      document.querySelectorAll('header-menu, products-mega-menu').forEach((menu) => {
        if (menu !== this && menu.close) menu.close();
      });
      this.openedByHover = byHover;
      this.details.open = true;
      this.summary.setAttribute('aria-expanded', 'true');
      this.classList.add('is-open');
    }

    close() {
      window.clearTimeout(this.openTimer);
      window.clearTimeout(this.closeTimer);
      this.openedByHover = false;
      if (!this.details || !this.details.open) return;
      this.details.open = false;
      this.summary.setAttribute('aria-expanded', 'false');
      this.classList.remove('is-open');
      this.nested && this.nested.forEach((nested) => (nested.open = false));
    }
  }

  class ProductsMegaMenu extends MenuDisclosure {
    constructor() {
      super();
      this.onTabIntent = (event) => this.activate(event.currentTarget.dataset.pmegaTab);
    }

    connectedCallback() {
      super.connectedCallback();
      this.tabs = this.querySelectorAll('[data-pmega-tab]');
      this.panels = this.querySelectorAll('[data-pmega-panel]');
      this.tabs.forEach((tab) => {
        tab.addEventListener('pointerenter', this.onTabIntent);
        tab.addEventListener('focus', this.onTabIntent);
      });
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.tabs && this.tabs.forEach((tab) => {
        tab.removeEventListener('pointerenter', this.onTabIntent);
        tab.removeEventListener('focus', this.onTabIntent);
      });
    }

    activate(index) {
      this.tabs.forEach((tab) => {
        const active = tab.dataset.pmegaTab === index;
        tab.closest('li')?.classList.toggle('is-active', active);
        tab.setAttribute('aria-expanded', String(active));
      });
      this.panels.forEach((panel) => {
        const active = panel.dataset.pmegaPanel === index;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });
    }
  }

  Multiverse.defineOnce('header-menu', MenuDisclosure);
  Multiverse.defineOnce('products-mega-menu', ProductsMegaMenu);
})();

// ---- header-drawer ----
// The drawer (mobile) menu block (blocks/header-drawer.liquid). Contract:
//   [data-drawer-toggle]   the hamburger button (aria-expanded mirrors the state)
//   [data-drawer-close]    the overlay and any close button inside the drawer
//   .mv-drawer__panel      the off-canvas panel; `is-open` on <header-drawer> slides it in
//   data-test-mode="true"  opens on load (editor convenience; no scroll lock or focus trap)
//   data-desktop-type      the header's desktop menu type; unless it is "drawer", the drawer
//                          closes itself when the viewport grows past 990px
//   [data-nested-mode="slide"] navs: their [data-drawer-submenu] <details> slide in as panels
//                          (`is-open` on the details after it opens, so CSS can transition);
//                          [data-drawer-back] closes the panel; the nearest scrolling ancestor
//                          (overflow-y auto/scroll) is scrolled to the top and gets
//                          `has-open-submenu` while a panel is open
//   body.mv-drawer-open    scroll lock while open
// Escape closes. Focus is trapped with Dawn's trapFocus/removeTrapFocus (assets/global.js).
// In the theme editor, selecting the block or any block inside it opens the drawer.
(() => {
  const SLIDE_MS = 320;

  class HeaderDrawer extends HTMLElement {
    constructor() {
      super();
      this.isOpen = false;
      // Bound once (see MenuDisclosure): a reconnect re-adds the same listener references.
      this.onClick = (event) => {
        if (event.target.closest('[data-drawer-toggle]')) {
          event.preventDefault();
          this.isOpen ? this.close() : this.open();
          return;
        }
        if (event.target.closest('[data-drawer-close]')) {
          event.preventDefault();
          this.close();
          return;
        }
        const back = event.target.closest('[data-drawer-back]');
        if (back) {
          event.preventDefault();
          this.closeSubmenu(back.closest('details'));
          return;
        }
        const summary = event.target.closest('summary');
        if (summary && this.contains(summary)) {
          const details = summary.parentElement;
          const nav = summary.closest('[data-nested-mode]');
          if (nav && nav.dataset.nestedMode === 'slide' && details.hasAttribute('data-drawer-submenu')) {
            event.preventDefault();
            details.open ? this.closeSubmenu(details) : this.openSubmenu(details);
          }
        }
      };
      this.onKeydown = (event) => {
        if (event.key !== 'Escape' || !this.isOpen) return;
        const openSlide = this.querySelector('[data-nested-mode="slide"] details[open].is-open');
        if (openSlide) {
          this.closeSubmenu(openSlide);
          openSlide.querySelector(':scope > summary')?.focus();
        } else {
          this.close();
        }
      };
      this.onResize = () => {
        if (this.isOpen && !this.testMode && window.innerWidth >= 990 && this.dataset.desktopType !== 'drawer') {
          this.close();
        }
      };
      this.onBlockSelect = (event) => {
        if (this.contains(event.target)) this.open({ lock: false });
      };
      this.onBlockDeselect = (event) => {
        if (this.contains(event.target) && !this.testMode) this.close();
      };
    }

    connectedCallback() {
      this.toggle = this.querySelector('[data-drawer-toggle]');
      this.panel = this.querySelector('.mv-drawer__panel');
      this.testMode = this.dataset.testMode === 'true';

      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      window.addEventListener('resize', this.onResize);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      if (this.testMode) this.open({ lock: false, focus: false });
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      if (this.isOpen) this.close();
    }

    open({ lock = true, focus = true } = {}) {
      if (this.isOpen) return;
      this.isOpen = true;
      this.classList.add('is-open');
      this.toggle?.setAttribute('aria-expanded', 'true');
      if (lock) document.body.classList.add('mv-drawer-open');
      if (focus && typeof trapFocus === 'function' && this.panel) {
        const first = this.panel.querySelector('button, a[href], input, [tabindex="0"]') || this.panel;
        trapFocus(this.panel, first);
      }
      this.dispatchEvent(new CustomEvent('mv:drawer:open', { bubbles: true }));
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.classList.remove('is-open');
      this.toggle?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('mv-drawer-open');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.toggle);
      this.querySelectorAll('details[open]').forEach((details) => {
        details.open = false;
        details.classList.remove('is-open');
      });
      this.querySelectorAll('.has-open-submenu').forEach((el) => el.classList.remove('has-open-submenu'));
      this.dispatchEvent(new CustomEvent('mv:drawer:close', { bubbles: true }));
    }

    // Nearest ancestor that scrolls (the drawer body, the panel, or an open slide submenu).
    scrollerOf(element) {
      let node = element.parentElement;
      while (node && node !== this) {
        const overflow = window.getComputedStyle(node).overflowY;
        if (overflow === 'auto' || overflow === 'scroll' || node.classList.contains('has-open-submenu')) return node;
        node = node.parentElement;
      }
      return this.panel;
    }

    openSubmenu(details) {
      const scroller = this.scrollerOf(details);
      window.clearTimeout(details.mvCloseTimer);
      details.open = true;
      if (scroller) {
        scroller.scrollTop = 0;
        scroller.classList.add('has-open-submenu');
      }
      window.requestAnimationFrame(() => {
        details.classList.add('is-open');
        details.querySelector('[data-drawer-back]')?.focus({ preventScroll: true });
      });
    }

    closeSubmenu(details) {
      if (!details) return;
      const scroller = this.scrollerOf(details);
      details.classList.remove('is-open');
      details.querySelectorAll('details[open]').forEach((nested) => {
        nested.open = false;
        nested.classList.remove('is-open');
      });
      window.clearTimeout(details.mvCloseTimer);
      details.mvCloseTimer = window.setTimeout(() => {
        details.open = false;
        if (scroller && !scroller.querySelector('details[open].is-open')) {
          scroller.classList.remove('has-open-submenu');
        }
      }, SLIDE_MS);
    }
  }

  Multiverse.defineOnce('header-drawer', HeaderDrawer);
})();

// ---- section-group ----
// Lays two other sections side by side (sections/section-group.liquid). Contract:
//   data-section-one / data-section-two  → section ids as pasted by the merchant. Accepts
//                                          "#shopify-section-<id>", "shopify-section-<id>" or "<id>".
//   [data-group-cell="one"|"two"]        → the grid cells the `.shopify-section` wrappers move into
//   [data-group-hint] / [data-group-missing] → editor-only hint, revealed when an id is not found
// On connect (and again on shopify:section:load / :reorder, when the editor re-renders things)
// each wrapper is moved into its cell; a placeholder comment marks where it came from so it goes
// back on disconnect (the editor removes and re-adds this section when its settings change).
// Moves are keyed by wrapper id and also keep the element, so both a wrapper the editor
// re-rendered (found by id) and one still inside a detached group (kept element) restore.
(() => {
  const PREFIX = 'shopify-section-';
  const normalise = (raw) => {
    const id = (raw || '').trim().replace(/^#/, '');
    if (!id) return '';
    return id.startsWith(PREFIX) ? id : PREFIX + id;
  };

  class SectionGroup extends HTMLElement {
    connectedCallback() {
      this.moved = this.moved || new Map();
      this.onEditorChange = this.onEditorChange || ((event) => {
        // Ignore reloads of the grouped sections' own content; their wrapper stays in place.
        if (event.target && this.contains(event.target)) return;
        this.mount();
      });
      document.addEventListener('shopify:section:load', this.onEditorChange);
      document.addEventListener('shopify:section:reorder', this.onEditorChange);
      this.mount();
    }

    disconnectedCallback() {
      document.removeEventListener('shopify:section:load', this.onEditorChange);
      document.removeEventListener('shopify:section:reorder', this.onEditorChange);
      this.restore();
    }

    mount() {
      const ids = { one: normalise(this.dataset.sectionOne), two: normalise(this.dataset.sectionTwo) };
      if (ids.two && ids.two === ids.one) ids.two = '';
      const missing = [];
      for (const key of ['one', 'two']) {
        const cell = this.querySelector(`[data-group-cell="${key}"]`);
        const target = ids[key] ? document.getElementById(ids[key]) : null;
        if (!cell || !target || target === this.closest('.shopify-section') || target.contains(this)) {
          missing.push(this.dataset[key === 'one' ? 'sectionOne' : 'sectionTwo'] || '(empty)');
          continue;
        }
        if (target.parentElement === cell) continue;
        if (!this.moved.has(ids[key])) {
          const placeholder = document.createComment(`section-group placeholder: ${ids[key]}`);
          target.parentNode.insertBefore(placeholder, target);
          this.moved.set(ids[key], { placeholder, el: target });
        }
        cell.appendChild(target);
      }
      const hint = this.querySelector('[data-group-hint]');
      if (hint) {
        hint.hidden = missing.length === 0;
        const list = hint.querySelector('[data-group-missing]');
        if (list) list.textContent = missing.join(', ');
      }
    }

    restore() {
      // When the editor re-renders this section, disconnectedCallback runs with the grouped
      // wrappers still inside the (now detached) group, so getElementById finds nothing; fall
      // back to the element we moved — a detached element can be reinserted at its placeholder.
      for (const [id, { placeholder, el }] of this.moved) {
        const target = document.getElementById(id) || el;
        if (!placeholder.parentNode) continue;
        if (target) placeholder.replaceWith(target);
        else placeholder.remove();
      }
      this.moved.clear();
    }
  }

  Multiverse.defineOnce('section-group', SectionGroup);
})();

// ---- scroll-to-top ----
// Fixed "back to top" button (sections/scroll-to-top-btn.liquid). Contract:
//   data-display-after → px scrolled before the button shows; 0 keeps it always visible
//   hidden (attribute) → toggled here on a passive, rAF-coalesced scroll listener
//   <button> child     → click / Enter / Space smooth-scroll to the top and move focus to #MainContent
(() => {
  class ScrollToTop extends HTMLElement {
    connectedCallback() {
      this.threshold = parseInt(this.dataset.displayAfter, 10) || 0;
      this.button = this.querySelector('button');
      this.frame = 0;
      this.onScroll = () => {
        if (this.frame) return;
        this.frame = window.requestAnimationFrame(() => {
          this.frame = 0;
          this.update();
        });
      };
      this.onClick = (event) => {
        event.preventDefault();
        this.scrollToTop();
      };
      if (this.button) this.button.addEventListener('click', this.onClick);
      if (this.threshold > 0) {
        window.addEventListener('scroll', this.onScroll, { passive: true });
      }
      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      if (this.button) this.button.removeEventListener('click', this.onClick);
      window.cancelAnimationFrame(this.frame);
      this.frame = 0;
    }

    update() {
      this.hidden = this.threshold > 0 && window.scrollY <= this.threshold;
    }

    scrollToTop() {
      const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
      const main = document.getElementById('MainContent');
      if (main) main.focus({ preventScroll: true });
    }
  }

  Multiverse.defineOnce('scroll-to-top', ScrollToTop);
})();

// ---- page-scroll-progress ----
// Fixed progress bar (sections/page-scroll-progress.liquid). Contract:
//   --progress (0–1) on the element   → the fill's width, written on a rAF-coalesced scroll/resize
//   --progress-top (px)               → for `.page-progress--top`: the bottom edge of whatever is
//                                       currently stuck at the top (sticky header / announcement
//                                       bar), so the bar sits just under it; 0 when nothing is
//   aria-valuenow                     → 0–100, kept in sync for assistive tech
(() => {
  const STUCK_SELECTOR = '.section-header.mv-header-sticky, .announcement-bar-section--sticky';
  const SETTLE_MS = 350;

  class PageScrollProgress extends HTMLElement {
    connectedCallback() {
      this.frame = 0;
      this.isTop = this.classList.contains('page-progress--top');
      this.onScroll = () => {
        if (this.frame) return;
        this.frame = window.requestAnimationFrame(() => {
          this.frame = 0;
          this.update();
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll);
      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      window.cancelAnimationFrame(this.frame);
      window.clearTimeout(this.settleTimer);
      this.frame = 0;
    }

    update() {
      this.measure();
      if (this.isTop) {
        // A sticky header slides in/out with a transition; re-measure once it has settled so the
        // bar ends up under its final position rather than where it was mid-slide.
        window.clearTimeout(this.settleTimer);
        this.settleTimer = window.setTimeout(() => this.measure(), SETTLE_MS);
      }
    }

    measure() {
      const doc = document.documentElement;
      const max = Math.max(doc.scrollHeight - window.innerHeight, 0);
      const progress = max ? Math.min(Math.max(window.scrollY / max, 0), 1) : 0;
      this.style.setProperty('--progress', progress.toFixed(4));
      this.setAttribute('aria-valuenow', String(Math.round(progress * 100)));
      if (this.isTop) {
        let top = 0;
        document.querySelectorAll(STUCK_SELECTOR).forEach((stuck) => {
          top = Math.max(top, stuck.getBoundingClientRect().bottom);
        });
        this.style.setProperty('--progress-top', `${Math.round(top)}px`);
      }
    }
  }

  Multiverse.defineOnce('page-scroll-progress', PageScrollProgress);
})();

// ---- redirect-to-page ----
// Live-store redirect (sections/redirect-to-page.liquid). Contract:
//   data-url → destination; location.replace keeps the redirecting page out of history.
// The section only renders this element outside the theme editor, and it also bails out if the
// editor's design-mode class is present, so previews never navigate away.
(() => {
  class RedirectToPage extends HTMLElement {
    connectedCallback() {
      const url = (this.dataset.url || '').trim();
      if (!url || window.Shopify?.designMode) return;
      window.location.replace(url);
    }
  }

  Multiverse.defineOnce('redirect-to-page', RedirectToPage);
})();
